import { withStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import '../../components/SelfInspect/styles.css';
import { CircularProgress } from '@material-ui/core';
import {
  getSelfInspect,
  saveSelfInspectAnswers,
  uploadSelfInspectImage,
  submitSelfInspect,
  deleteSelfInspectImage,
  addQuickVueLog,
  addOutbuilding,
  deleteOutbuilding,
} from '../../services';
import { selfInspectQuestions } from './mock-questions/mockSelfInspectQuestions';
import { selfInspectError } from './mock-questions/mockSelfInspectQuestions';
import { remoteQuestions } from './mock-questions/mockRemoteQuestions';
import { quickVueFarmQuestions } from './mock-questions/mockQuickVueFarmQuestions';
import { useRef } from 'react';
import { isMobile } from '../../helpers/os';
import { filterFormAnswers, getComponentToRender, getFormAnswer } from './quickVueUtils';
import { OUTBUILDING_STEPS_TEMPLATE } from '../../components/SelfInspect/FarmOutbuildings/FarmOutbuildingsCard';

const createImageObjectFromFormImage = formImage => {
  const fileType = formImage.imageURI.split('.').slice(-1);

  const imageUri = formImage.imageURI;

  return {
    imageUri,
    src: imageUri,
    fileName: `${formImage.formImageId}.${fileType}`,
    label: null,
    rotateDegree: 0,
    formImageId: formImage.formImageId,
    /*
        data: Object.assign(file, {
            preview: URL.createObjectURL(file),
          }), */
  };
};

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;

  const orientation = width > height ? 'landscape' : 'portrait';

  return {
    width,
    height,
    orientation,
  };
};

const TRANSITION_TIME = '0.25s';
const PAGE_PADDING = 10;
const MAX_CARD_WIDTH = 400;

const styles = theme => ({
  wrapper: {
    width: '100vw',
    height: 'var(--screen-height)',
    display: 'flex',
    boxSizing: 'border-box',

    // prevent overscroll on mobile
    overscrollBehaviorY: 'none',
    position: 'fixed',
    overflow: 'hidden',

    '& *': {
      boxSizing: 'border-box',
      userSelect: 'none',
    },
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: `linear-gradient(rgba(47, 137, 217), rgba(255, 255, 255, 0) 50%)`,
    paddingTop: PAGE_PADDING,
    paddingBottom: PAGE_PADDING,
  },

  headerBar: {
    width: '100%',
    maxWidth: MAX_CARD_WIDTH,
    margin: 'auto',
    marginBottom: 10,
    padding: `0px ${PAGE_PADDING}px`,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    overflow: 'hidden',
    maxWidth: '100%',
    position: 'relative',
  },

  content: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    maxWidth: MAX_CARD_WIDTH,
    margin: 'auto',
    transition: `all ${TRANSITION_TIME} ease-out`,
    padding: `0px ${PAGE_PADDING}px`,
  },

  loadingBox: {
    flex: 1,
    minHeight: 150,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },

  orderStatusCardsContainer: {
    flexWrap: 'wrap',
  },
});

const ACTION_HISTORY_TYPES = {
  START: 'START',
  // END: 'END',
  RESUME: 'RESUME',
  // SAVE: 'SAVE',
  SUBMIT: 'SUBMIT',
  GO_BACK: 'GO_BACK',
  CONFIRM_ANSWER: 'CONFIRM_ANSWER',
  CONFIRM_ANSWER_SUCCESS: 'CONFIRM_ANSWER_SUCCESS',
  CONFIRM_ANSWER_FAILED: 'CONFIRM_ANSWER_FAILED',

  DELETE_IMAGE: 'DELETE_IMAGE',
  DELETE_IMAGE_SUCCESS: 'DELETE_IMAGE_SUCCESS',
  DELETE_IMAGE_FAILED: 'DELETE_IMAGE_FAILED',

  UPLOAD_IMAGE: 'UPLOAD_IMAGE',
  UPLOAD_IMAGE_SUCCESS: 'UPLOAD_IMAGE_SUCCESS',
  UPLOAD_IMAGE_FAILED: 'UPLOAD_IMAGE_FAILED',
  // RETRY_IMAGE: 'RETRY_IMAGE',
  // RETRY_IMAGE_SUCCESS: 'RETRY_IMAGE_SUCCESS',
  // RETRY_IMAGE_FAIL: 'RETRY_IMAGE_FAIL',
};

const SelfInspectPortal = withStyles(styles)(props => {
  const { classes, match } = props;
  const [steps, setSteps] = useState([]);
  const [outbuildingSteps, setOutbuildingSteps] = useState([]);
  const [frozenSteps, setFrozenSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [screenOrientation, setScreenOrientation] = useState(getWindowDimensions().orientation);
  const [cardWidth, setCardWidth] = useState();
  const [pendingSaves, setPendingSaves] = useState([]);
  const [failedSaves, setFailedSaves] = useState([]);
  const pendingSavesRef = useRef([]);
  const failedSavesRef = useRef([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [cssRerenderKey, setCssRerenderKey] = useState(1);

  const filteredSteps = getFilteredSteps(steps, 'render');

  const contentContainerRef = useRef();
  const quickVueInfoRef = useRef();

  const createQuickVueLog = async (logType, logData) => {
    const memoryKeys = ['jsHeapSizeLimit', 'totalJSHeapSize', 'usedJSHeapSize'];
    const memory = performance.memory
      ? memoryKeys.reduce((acc, key) => {
          acc[key] = performance.memory[key] ? performance.memory[key] / 1000000 : null;
          return acc;
        }, {})
      : null;

    const data = JSON.stringify({
      ...logData,
      memory,
    });

    const quickVueLog = {
      orderLineGuid: match.params.orderKey,
      logType,
      data,
      memory,
    };

    await addQuickVueLog(quickVueLog);
  };

  const modifyStep = (step, { cardNumber, questionId, value }) => {
    if (!!step.questions) {
      const question = step.questions.find(q => q.questionId === questionId);

      if (question.type === 'number' && !value) {
        value = '';
      }

      if (question.maxLength) {
        if (value && value.length > 4) {
          question.answerValue = value.substring(0, question.maxLength);
        } else {
          question.answerValue = value;
        }
      } else {
        question.answerValue = value;
      }

      if (question.minLength) {
        if (question.answerValue) {
          question.error = question.answerValue.length < question.minLength ? true : false;
        } else {
          question.error = true;
        }
      }

      if (question.type === 'select') {
        question.error = !question.answerValue ? true : false;
      }
    } else {
      step.answerValue = value;
    }

    return step;
  };

  const updateAnswer = ({ cardNumber, questionId, value, isOutbuildingStep = false }) => {
    const updatedSteps = cloneDeep(isOutbuildingStep ? outbuildingSteps : steps);
    const updateStepIndex = updatedSteps.findIndex(step => step.cardNumber === cardNumber);

    const updatedStep = modifyStep(cloneDeep(updatedSteps[updateStepIndex]), {
      cardNumber,
      questionId,
      value,
    });

    updatedSteps[updateStepIndex] = updatedStep;

    isOutbuildingStep ? setOutbuildingSteps(updatedSteps) : setSteps(updatedSteps);
  };

  const resetAnswer = ({ cardNumber, questionId }) => {
    const updatedSteps = steps.slice();
    const cardIndex = updatedSteps.findIndex(step => step.cardNumber === cardNumber);

    if (!!updatedSteps[cardIndex].questions) {
      const questionIndex = updatedSteps[cardIndex].questions.findIndex(q => q.questionId === questionId);

      if (questionId) {
        const questionReset = frozenSteps[cardIndex].questions[questionIndex].answerValue;
        updatedSteps[cardIndex].questions[questionIndex].answerValue = questionReset;
      } else {
        const questionsReset = cloneDeep(frozenSteps[cardIndex].questions);
        updatedSteps[cardIndex].questions = questionsReset;
      }
    } else {
      const cardReset = cloneDeep(frozenSteps[cardIndex]);
      updatedSteps[cardIndex] = cardReset;
    }

    setSteps(updatedSteps);
  };

  const deleteImage = async (formImageId, orderFormHeaderId) => {
    try {
      createQuickVueLog(ACTION_HISTORY_TYPES.DELETE_IMAGE, {
        formImageId,
        currentStep,
      });
      await deleteSelfInspectImage(match.params.orderKey, formImageId, orderFormHeaderId);
      createQuickVueLog(ACTION_HISTORY_TYPES.DELETE_IMAGE_SUCCESS, {
        formImageId,
        currentStep,
      });
    } catch (error) {
      createQuickVueLog(ACTION_HISTORY_TYPES.DELETE_IMAGE_FAILED, {
        formImageId,
        currentStep,
      });
      throw error;
    }
  };

  const goBack = () => {
    createQuickVueLog(ACTION_HISTORY_TYPES.GO_BACK, {
      currentStep,
    });

    setCurrentStep(currentStep - 1);
  };

  const submitInspection = async cardNumber => {
    await submitSelfInspect(match.params.orderKey);
    confirmAnswer({
      cardNumber,
      skipLog: true,
    });
  };

  const addAdditionalOutbuilding = async (numberOfOutbuildings = 1) => {
    const newOutbuildings = await addOutbuilding(match.params.orderKey, numberOfOutbuildings);

    const lastOutbuilding = outbuildingSteps[outbuildingSteps.length - 1];
    const lastOutbuildingNumber = lastOutbuilding?.outbuildingNumber ?? 0;
    const nextStartingOutbuildIndex = lastOutbuildingNumber;

    // console.log({
    //   numberOfOutbuildings,
    //   newOutbuildings,
    //   lastOutbuilding,
    //   lastOutbuildingNumber,
    //   nextStartingOutbuildIndex,
    // });

    const newInitializedOutbuildingSteps = [...Array(numberOfOutbuildings)].flatMap((_, outbuildingIndex) => {
      const steps = cloneDeep(OUTBUILDING_STEPS_TEMPLATE).map((step, stepIndex) => {
        let stepWithOrderFormHeaderId = {
          ...step,
          orderFormHeaderId: newOutbuildings[outbuildingIndex].orderFormHeaderId,
          formAnswers: [],
        };

        return initializeOutbuildingStep(
          stepWithOrderFormHeaderId,
          stepIndex,
          nextStartingOutbuildIndex + outbuildingIndex,
        );
      });

      return steps;
    });

    setOutbuildingSteps([...outbuildingSteps, ...newInitializedOutbuildingSteps]);

    // const newOutbuildingSteps = [...Array(numberOfOutbuildings)].flatMap((_, outbuildingIndex) => {
  };

  const removeOutbuilding = async orderFormHeaderId => {
    const success = await deleteOutbuilding(match.params.orderKey, orderFormHeaderId);
    setOutbuildingSteps(prev => prev.filter(step => step.orderFormHeaderId !== orderFormHeaderId));

    return success;
  };

  const confirmAnswer = async ({
    cardNumber,
    answers,
    questionId,
    answerValue,
    skipLog = false,
    isOutbuildingStep = false,
  }) => {
    !skipLog &&
      createQuickVueLog(ACTION_HISTORY_TYPES.CONFIRM_ANSWER, {
        cardNumber,
        questionId,
        answerValue,
        currentStep,
        answers,
      });

    const step = (isOutbuildingStep ? outbuildingSteps : steps).find(s => s.cardNumber === cardNumber);
    const stepQuestionId = questionId;

    // console.log({
    //   cardNumber,
    //   answers,
    //   questionId,
    //   answerValue,
    //   skipLog,
    //   isOutbuildingStep,
    //   step,
    //   stepQuestionId,
    // });

    if (!answers && step.questions) {
      answers = [];
      step.questions.map(({ questionId, type, answerValue }) => {
        const answer = {
          questionId: stepQuestionId ?? questionId,
          // orderFormHeaderId: step.orderFormHeaderId,
        };

        if (type === 'select') {
          answer.answerValueId = answerValue;
        } else if (type === 'text' || type === 'textarea' || type === 'number') {
          answer.answerText = answerValue;
        } else if (type === 'checkbox') {
          if (stepQuestionId) {
            answer.answerValueId = answerValue ? questionId : null;
          } else {
            answer.answerValueId = answerValue ? 3 : 4;
          }
        }

        if (answer.answerText || answer.answerValueId) {
          answers.push(answer);
        }
      });
    } else if (!answers && questionId && answerValue) {
      if (step.type === 'yesno') {
        answers = [
          {
            questionId: questionId,
            answerValueId: answerValue,
            // orderFormHeaderId: step.orderFormHeaderId,
          },
        ];
      } else if (step.type === 'textarea') {
        answers = [
          {
            questionId: questionId,
            answerText: answerValue,
            // orderFormHeaderId: step.orderFormHeaderId,
          },
        ];
      } else if (step.type === 'image-radios') {
        answers = [
          {
            questionId: questionId,
            answerValueId: answerValue,
            // orderFormHeaderId: step.orderFormHeaderId,
          },
        ];
      }
    } else if (!answers && questionId && step.type === 'image-checkboxes') {
      answers = [];
    }

    if (step.type === 'take-picture' && answerValue) {
      answerValue.map(async (image, imageIndex) => {
        if (!image.formImageId && !image.uploadInitiated) {
          const processKey = `${cardNumber}_${questionId}_${imageIndex}_${image.fileName}`;

          const logInfo = {
            processKey,
            cardNumber,
            questionId,
            fileName: image.fileName,
          };

          const saveObject = {
            ...logInfo,
            complete: false,
            error: false,
            //
            orderKey: match.params.orderKey,
            image,
            // orderFormHeaderId: step.orderFormHeaderId,
          };

          pendingSavesRef.current.push(saveObject);
          setPendingSaves(pendingSavesRef.current);

          try {
            image.uploadInitiated = true;
            image.loading = true;

            !skipLog && createQuickVueLog(ACTION_HISTORY_TYPES.UPLOAD_IMAGE, logInfo);

            const response = await uploadSelfInspectImage(
              saveObject.orderKey,
              questionId,
              image,
              step.orderFormHeaderId,
            );

            image.loading = false;
            saveObject.complete = true;

            if (response.formImageId) {
              image.formImageId = response.formImageId;

              !skipLog &&
                createQuickVueLog(ACTION_HISTORY_TYPES.UPLOAD_IMAGE_SUCCESS, {
                  ...logInfo,
                  formImageId: image.formImageId,
                });
            } else {
              throw new Error('Form image failed to upload');
            }
          } catch (error) {
            image.error = true;
            saveObject.error = true;
            saveObject.retryCount = 0;

            failedSavesRef.current.push(saveObject);
            setFailedSaves(failedSavesRef.current);

            !skipLog &&
              createQuickVueLog(ACTION_HISTORY_TYPES.UPLOAD_IMAGE_FAILED, {
                ...logInfo,
                message: error.message,
              });
          }

          pendingSavesRef.current = pendingSavesRef.current.filter(ps => ps.processKey !== processKey);
          setPendingSaves(pendingSavesRef.current);
        }
      });
    }

    if (answers && (answers.length > 0 || step.type === 'image-checkboxes')) {
      (async () => {
        try {
          await saveSelfInspectAnswers(match.params.orderKey, answers, questionId, step.orderFormHeaderId);
          !skipLog &&
            createQuickVueLog(ACTION_HISTORY_TYPES.CONFIRM_ANSWER_SUCCESS, {
              cardNumber,
              questionId,
              answerValue,
              currentStep,
              answers,
            });
        } catch (error) {
          !skipLog &&
            createQuickVueLog(ACTION_HISTORY_TYPES.CONFIRM_ANSWER_FAILED, {
              cardNumber,
              questionId,
              answerValue,
              currentStep,
              answers,
            });
          throw error;
        }
      })();
    }

    !isOutbuildingStep && setCurrentStep(currentStep + 1);
  };

  const renderStep = (step, stepIndex) => {
    // if (failedToLoad) return null;

    // const step = steps;

    // if (!step) return;

    const isActive = stepIndex === currentStep;
    const isBefore = stepIndex < currentStep;
    const isAfter = stepIndex > currentStep;

    const Component = getComponentToRender(step.type);

    if (step.questionId === 239 && !step.answerValue && !step.edited) {
      step.answerValue = quickVueInfoRef.current.formAnswers.find(fa => fa.questionId === 239)?.answerText;
      step.edited = true;
    }

    const questionLabel =
      typeof step.questionLabel === 'function'
        ? step.questionLabel(step, stepIndex, quickVueInfoRef.current)
        : step.questionLabel;

    const questionImage =
      typeof step.questionImage === 'function'
        ? step.questionImage(step, stepIndex, quickVueInfoRef.current)
        : step.questionImage;

    if (!Component) return null;

    const stepProps = {
      updateAnswer,
      resetAnswer,
      confirmAnswer,
      selfInspectIntro: quickVueInfoRef.current,
      quickVueInfo: quickVueInfoRef.current,
      orderInfo: quickVueInfoRef.current,
      stepIndex,
      submitInspection,
      addAdditionalOutbuilding,
      removeOutbuilding,
      isSaving,
      isLoading,
      pendingSaves,
      failedSaves,
      deleteImage,
      goBack,
      //
      questionLabel,
      questionImage,

      isActive,
      isBefore,
      isAfter,

      outbuildingSteps,
    };

    const beforeOffset = -cardWidth;
    const afterOffset = window.innerWidth;
    const activeOffset = window.innerWidth / 2 - cardWidth / 2;

    const computedStyle = {
      width: cardWidth,
      position: 'absolute',
      top: 0,
      bottom: 0,
      visibility: isActive ? 'visible' : 'hidden',
      left: isBefore ? beforeOffset : isAfter ? afterOffset : activeOffset,
    };

    return (
      <div
        key={step.cardNumber}
        //
        className={classes.content}
        style={computedStyle}
      >
        <Component
          //
          {...step}
          {...stepProps}
        />
      </div>
    );
  };

  const findResumeStep = (filteredStepsList = [], formAnswers = [], debug = false) => {
    const reverseStepList = cloneDeep(filteredStepsList).reverse();

    const reverseStepIndex = reverseStepList.findIndex((step, stepIndex) => {
      // skip if the question is not resume-able (default to true if the property is not set)
      if (step.allowResume !== undefined && !step.allowResume) {
        debug && console.log(`${stepIndex}: disableResume`);
        return false;
      }

      // check if there are nested questions
      if (!!step.questions) {
        const nestedResumeStep = findResumeStep(step.questions, formAnswers);
        if (nestedResumeStep !== -1) {
          debug && console.log(`${stepIndex}: nestedResumeStep`);
          return true;
        }
      }

      // does not require an answer, move on
      if (!step.questionId) {
        debug && console.log(`${stepIndex}: no questionId`);
        return false;
      }

      const fa = getFormAnswer({
        questionId: step.questionId,
        orderFormHeaderId: step.orderFormHeaderId,
        outbuildingNumber: step.outbuildingNumber,
        formAnswers,
      });

      // if no answer exists yet, move on
      if (!fa) {
        debug && console.log(`${stepIndex}: no formAnswer`);
        return false;
      }

      // picture questions that have an image
      if (step.type === 'take-picture') {
        debug && console.log(`${stepIndex}: take-picture: ${fa.formImages.length}`);
        return fa.formImages.length;
      }

      // if the form answer has an answer
      debug && console.log(`${stepIndex}: default: ${!!fa.answerValueId}`);
      return !!fa.answerValueId;
    });

    return reverseStepIndex === -1 ? -1 : filteredStepsList.length - reverseStepIndex - 1;
  };

  // leave as ES5 function for hoisting
  function getFilteredSteps(stepsList) {
    return stepsList.filter((step, stepIndex, self) => {
      return !step.showIf || step.showIf(stepsList, step, stepIndex);
    });
  }

  const calculateMaxCardWidth = () => {
    const refWidth = contentContainerRef.current.clientWidth; // - PAGE_PADDING * 2;
    const maxCardWidth = refWidth < MAX_CARD_WIDTH ? refWidth : MAX_CARD_WIDTH;

    setCardWidth(maxCardWidth);
  };

  const initializeStep = step => {
    const {
      //
      questionId,
      questions,
      cardNumber,
      type,
      answerValue,
      //
      // outbuilding params
      orderFormHeaderId,
      outbuildingNumber,
    } = step;

    switch (type) {
      case 'yesno': {
        const answer = getFormAnswer({
          questionId,
          orderFormHeaderId,
          outbuildingNumber,
          quickVueInfo: quickVueInfoRef.current,
        });

        modifyStep(step, {
          cardNumber,
          questionId: questionId,
          value: answer ? answer.answerValueId === 3 : null,
        });

        break;
      }
      case 'yesno-edit': {
        break;
      }
      case 'textarea': {
        const answer = getFormAnswer({
          questionId,
          orderFormHeaderId,
          outbuildingNumber,
          quickVueInfo: quickVueInfoRef.current,
        });

        modifyStep(step, {
          cardNumber,
          questionId: questionId,
          value: answer ? answer.answerText : null,
        });

        break;
      }
      case 'multi-input': {
        questions.map(q => {
          const answer = getFormAnswer({
            questionId: q.questionId,
            orderFormHeaderId,
            outbuildingNumber,
            quickVueInfo: quickVueInfoRef.current,
          });

          modifyStep(step, {
            cardNumber,
            questionId: q.questionId,
            value: answer ? answer.answerValueId ?? answer.answerText : null,
          });
        });
        break;
      }
      case 'image-checkboxes': {
        if (questionId) {
          const formAnswers = filterFormAnswers({
            questionId,
            orderFormHeaderId,
            outbuildingNumber,
            quickVueInfo: quickVueInfoRef.current,
          });

          !!formAnswers &&
            formAnswers.map(fa => {
              modifyStep(step, {
                cardNumber,
                questionId: fa.answerValueId,
                value: true,
              });
            });
        } else {
          const questionIds = Array.from(questions, q => q.questionId);

          const formAnswers = filterFormAnswers({
            questionIds,
            orderFormHeaderId,
            outbuildingNumber,
            quickVueInfo: quickVueInfoRef.current,
          });

          !!formAnswers &&
            formAnswers.map(fa => {
              modifyStep(step, {
                cardNumber,
                questionId: fa.questionId,
                value: fa.answerValueId === 3 ? true : false,
              });
            });
        }

        break;
      }
      case 'image-radios': {
        const answer = getFormAnswer({
          questionId,
          orderFormHeaderId,
          outbuildingNumber,
          quickVueInfo: quickVueInfoRef.current,
        });

        modifyStep(step, {
          cardNumber,
          questionId: questionId,
          value: answer ? answer.answerValueId : null,
        });

        break;
      }
      case 'take-picture': {
        const formAnswers = filterFormAnswers({
          questionId,
          orderFormHeaderId,
          outbuildingNumber,
          requireImages: true,
          quickVueInfo: quickVueInfoRef.current,
        });

        const answers = [];

        !!formAnswers &&
          formAnswers.map(answer => {
            answer.formImages.map(image => {
              answers.push(createImageObjectFromFormImage(image));
            });
          });

        if (answerValue) {
          answers.push(...answerValue);
        }

        modifyStep(step, {
          cardNumber,
          questionId,
          value: answers,
        });

        break;
      }

      case 'farm-structures': {
        const { outbuildings } = quickVueInfoRef.current;

        // modifyStep(step, {
        //   cardNumber,
        //   outbuildings,
        // });
        break;
      }

      case 'title': {
        break;
      }
      case 'idbadge': {
        break;
      }
      default: {
        break;
      }
    }

    return step;
  };

  const initializeOutbuildingStep = (step, stepIndex, outbuildingIndex) => {
    const outbuilding = quickVueInfoRef.current.outbuildings[outbuildingIndex];

    // const orderFormHeaderId = outbuilding?.orderFormHeaderId ?? step.orderFormHeaderId;
    const orderFormHeaderId = step.orderFormHeaderId ?? outbuilding?.orderFormHeaderId;

    const stepWithIdentity = {
      ...step,

      orderFormHeaderId,
      cardNumber: `OB-${outbuildingIndex * OUTBUILDING_STEPS_TEMPLATE.length + stepIndex + 1}`,
      outbuildingNumber: outbuildingIndex + 1,
    };

    return initializeStep(stepWithIdentity);
  };

  const handleUnfinishedUploads = async () => {
    if (!failedSavesRef.current.length) {
      return;
    }

    const MAX_RETRIES = 120; // 120 retries * 5 second sleep = 10 minute max retry period

    failedSavesRef.current.forEach(async failedSaveObj => {
      let retryCount = 0;

      while (!failedSaveObj.image.formImageId && retryCount < MAX_RETRIES) {
        try {
          failedSaveObj.complete = false;
          const response = await uploadSelfInspectImage(
            failedSaveObj.orderKey,
            failedSaveObj.questionId,
            failedSaveObj.image,
          );
          failedSaveObj.complete = true;

          if (response.formImageId) {
            failedSaveObj.error = false;
            failedSaveObj.image.formImageId = response.formImageId;
            pendingSavesRef.current = pendingSavesRef.current.filter(ps => ps.processKey !== failedSaveObj.processKey);
            failedSavesRef.current = failedSavesRef.current.filter(ps => ps.processKey !== failedSaveObj.processKey);
          } else {
            throw new Error('Form image failed to upload');
          }

          setPendingSaves(pendingSavesRef.current);
          setFailedSaves(failedSavesRef.current);
        } catch (error) {
          failedSaveObj.image.error = true;
          failedSaveObj.error = true;
          failedSaveObj.retryCount++;

          await new Promise(resolve => setTimeout(resolve, 5000));

          // failedSavesRef.current.push(saveObject);
          // setFailedSaves(failedSavesRef.current);
        }
        retryCount++;
      }
    });
  };

  const getDeviceInfo = async () => {
    try {
      const ua = await navigator.userAgentData.getHighEntropyValues([
        'architecture',
        'model',
        'platform',
        'platformVersion',
        'fullVersionList',
      ]);

      return ua;
    } catch (error) {
      return {};
    }
  };

  useEffect(() => {
    const setupSelfInspect = async () => {
      try {
        if (!match.params.orderKey) {
          throw new Error('No order key provided');
        }

        // const data = await getDashboardCounts('ADMIN');
        // setStatusCards(data.sort((a, b) => (a.seq > b.seq ? 1 : -1)));
        quickVueInfoRef.current = await getSelfInspect(match.params.orderKey);

        setIsLoading(false);

        let startingSteps = quickVueInfoRef.current.remote
          ? remoteQuestions
          : quickVueInfoRef.current.farm
          ? quickVueFarmQuestions
          : selfInspectQuestions;
        // let startingSteps = quickVueFarmQuestions;

        const clonedSteps = cloneDeep(startingSteps);
        const newFrozenSteps = cloneDeep(startingSteps);

        const newSteps = clonedSteps.map((step, stepIndex) => {
          return initializeStep(step);
        });

        const initializedOutbuildingSteps =
          quickVueInfoRef.current.outbuildings?.flatMap((outbuilding, outbuildingIndex) => {
            const steps = cloneDeep(OUTBUILDING_STEPS_TEMPLATE).map((step, stepIndex) => {
              return initializeOutbuildingStep(step, stepIndex, outbuildingIndex);
            });

            return steps;
          }) ?? [];

        const filteredSteps = getFilteredSteps(newSteps, 'useEffect');
        const resumeStep = findResumeStep(filteredSteps, quickVueInfoRef.current.formAnswers);

        const deviceInfo = await getDeviceInfo();

        await createQuickVueLog(resumeStep === -1 ? ACTION_HISTORY_TYPES.START : ACTION_HISTORY_TYPES.RESUME, {
          resumeStep,
          stepsLength: newSteps.length,
          filteredStepsLength: filteredSteps.length,
          deviceInfo,
        });

        setCurrentStep(resumeStep === -1 ? 0 : resumeStep);
        setSteps(newSteps);
        setFrozenSteps(newFrozenSteps);

        setOutbuildingSteps(initializedOutbuildingSteps);
      } catch (error) {
        console.log('error', error);
        setIsLoading(false);

        setFailedToLoad(true);
        setSteps(selfInspectError);
        setFrozenSteps(selfInspectError);
      }
    };

    setupSelfInspect();
  }, []);

  useEffect(() => {
    if (contentContainerRef.current) {
      calculateMaxCardWidth();
    }
  }, [contentContainerRef.current]);

  useEffect(() => {
    const handleResize = () => {
      //
      const { orientation } = getWindowDimensions();
      setScreenOrientation(orientation);

      calculateMaxCardWidth();
    };

    if (isMobile()) {
      // Detect whether device supports orientationchange event, otherwise fall back to
      // the resize event.
      var supportsOrientationChange = 'onorientationchange' in window,
        orientationEvent =
          // supportsOrientationChange
          false ? 'orientationchange' : 'resize';

      window.addEventListener(
        orientationEvent,
        function() {
          setTimeout(() => {
            // wait til the end of the resize event (toss the function on the top of the event queue so it doesn't fire right away)
            setCssRerenderKey(window.innerHeight);
          }, 0);
        },
        false,
      );
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    const currentStepObject = filteredSteps[currentStep];

    if (!currentStepObject || !currentStepObject.handleUnfinishedUploads || pendingSaves.length) {
      return;
    }

    handleUnfinishedUploads();
  }, [currentStep, steps, frozenSteps, pendingSaves]);

  return (
    <div className={classes.wrapper} key={cssRerenderKey}>
      <div className={classes.main}>
        <div
          //
          ref={contentContainerRef}
          className={classes.contentContainer}
        >
          {isLoading ? (
            <div className={classes.loadingBox}>
              <CircularProgress style={{ color: '#2F89D9' }} />
              Loading survey
            </div>
          ) : (
            filteredSteps.map(renderStep)
          )}
        </div>
      </div>
    </div>
  );
});

SelfInspectPortal.propTypes = {};

SelfInspectPortal.defaultProps = {};

export default SelfInspectPortal;
